import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  ArrowPathIcon,
  MapPinIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import { fetchPublicSponsors } from "../../features/admin/SponsorSlice";

const PAGE_SIZE = 12;

const getMediaUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${process.env.REACT_APP_API_URL}media/${url}`;
};

const SponsorCard = ({ item }) => (
  <Link
    to={`/nha-tai-tro/${item.id}`}
    className="group bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-lg transition overflow-hidden flex flex-col"
  >
    <div className=" h-40 p-2 bg-[#fbfbfb] flex items-center justify-center ">
      {item.logo ? (
        <img
          src={getMediaUrl(item.logo)}
          alt={item.name}
          className="h-full w-full object-contain group-hover:scale-105 transition"
        />
      ) : (
        <span className="text-gray-400 text-sm">Chưa có logo</span>
      )}
    </div>

    <div className="p-4 flex-1 flex flex-col">
      <h3 className="text-base font-bold text-gray-900 group-hover:text-primary transition line-clamp-2">
        {item.name}
      </h3>

      {item.businessField && (
        <p className="text-sm text-gray-600 mt-2 whitespace-pre-line line-clamp-3">
          {item.businessField}
        </p>
      )}

      <div className="mt-4 space-y-2 text-sm text-gray-600">
        {item.phoneNumber && (
          <p className="flex gap-2">
            <PhoneIcon className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{item.phoneNumber}</span>
          </p>
        )}

        {item.address && (
          <p className="flex gap-2">
            <MapPinIcon className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="line-clamp-2">{item.address}</span>
          </p>
        )}
      </div>

      <div className="mt-auto pt-5 text-primary font-semibold text-sm">
        Xem chi tiết →
      </div>
    </div>
  </Link>
);

const Sponsors = () => {
  const dispatch = useDispatch();
  const { publicSponsors, publicStatus, publicError } = useSelector(
    (state) => state.sponsors,
  );

  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (publicStatus === "idle") {
      dispatch(fetchPublicSponsors());
    }
  }, [dispatch, publicStatus]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue]);

  const filteredSponsors = useMemo(() => {
    const keyword = searchValue.trim().toLowerCase();

    const sponsors = (publicSponsors || [])
      .filter((item) => item?.status !== false)
      .sort((a, b) => {
        const priorityA = Number(a.priority ?? 0);
        const priorityB = Number(b.priority ?? 0);

        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        return new Date(b.createdAt) - new Date(a.createdAt);
      });

    if (!keyword) return sponsors;

    return sponsors.filter((item) =>
      [
        item.name,
        item.businessField,
        item.address,
        item.phoneNumber,
        item.email,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword)),
    );
  }, [publicSponsors, searchValue]);

  const totalPages = Math.ceil(filteredSponsors.length / PAGE_SIZE) || 1;

  const paginatedSponsors = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredSponsors.slice(start, start + PAGE_SIZE);
  }, [filteredSponsors, currentPage]);

  const handleChangePage = (page) => {
    if (page < 1 || page > totalPages) return;

    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (publicStatus === "loading") {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (publicStatus === "failed") {
    return (
      <div className="container mx-auto max-w-[1200px] px-4 py-10">
        <div className="p-4 text-red-500 bg-red-50 rounded-md">
          Đã xảy ra lỗi: {publicError?.messages || "Không thể tải nhà tài trợ"}
        </div>
      </div>
    );
  }

  return (
    <main className="bg-white">
      <section className="bg-[#fafafa] py-12">
        <div className="container mx-auto max-w-[1200px] px-4 text-center">
          <p className="uppercase tracking-[0.18em] text-2xl md:text-5xl text-primary font-extrabold">
            Nhà tài trợ
          </p>
          <h1 className="text-2xl md:text-4xl font-bold mt-4">
            Tình đồng hương trên đất Tây Đô
          </h1>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
            Trân trọng giới thiệu các đơn vị, doanh nghiệp và cá nhân đã đồng
            hành cùng cộng đồng.
          </p>
        </div>
      </section>

      <section className="container mx-auto max-w-[1200px] px-4 py-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl font-bold">Danh sách nhà tài trợ</h2>
            <p className="text-sm text-gray-500 mt-1">
              Bấm vào từng nhà tài trợ để xem thông tin và giới thiệu chi tiết.
            </p>
          </div>

          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Tìm nhà tài trợ..."
            className="input input-bordered w-full md:max-w-xs bg-white"
          />
        </div>

        {filteredSponsors.length === 0 ? (
          <div className="text-center text-gray-500 py-16 bg-[#fafafa] rounded-lg">
            Chưa có nhà tài trợ để hiển thị.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {paginatedSponsors.map((item) => (
                <SponsorCard key={item.id} item={item} />
              ))}
            </div>

            <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mt-10">
              <div className="text-sm text-gray-600">
                Trang{" "}
                <span className="font-semibold text-primary">
                  {currentPage}
                </span>{" "}
                / <span className="font-semibold">{totalPages}</span>
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={currentPage === 1}
                    onClick={() => handleChangePage(currentPage - 1)}
                  >
                    Trước
                  </button>

                  {Array.from({ length: totalPages }).map((_, index) => {
                    const page = index + 1;

                    return (
                      <button
                        key={page}
                        type="button"
                        className={`btn btn-sm ${
                          currentPage === page ? "btn-primary" : "btn-outline"
                        }`}
                        onClick={() => handleChangePage(page)}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={currentPage === totalPages}
                    onClick={() => handleChangePage(currentPage + 1)}
                  >
                    Sau
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
};

export default Sponsors;
